import { useCallback, useEffect, useRef, useState} from 'react';
import useMultiOpeningMatStore from '../../stores/useMultiOpeningMatStore';
import { computeOpeningRects, getCenteredCrop, isOvalDecklePaper, isCircleDecklePaper } from '../../lib/multiOpeningMatProducts';
import PhotoCropper from './PhotoCropper';
import Loading from '../common/Loading';
import styles from './MultiOpeningMatVisualization.module.css';

const getShapeStyle = (openingType) => {
  if (openingType === 'oval' || openingType === 'circle') {
    return { borderRadius: '50%' };
  }
  return {};
};

// A 'circle' opening must render as a true circle regardless of the print's
// own aspect ratio (e.g. a circle cut into a 5x7 opening). Since the mat's
// on-screen % width/height are relative to matWidth/matHeight respectively,
// we size the circle from the print's shorter physical dimension and center
// it within the opening's allotted rect, rather than stretching the full
// (possibly non-square) rect with border-radius: 50% — which produces an oval.
const getCircleRect = (rect, matWidth, matHeight) => {
  const diameterIn = Math.min(rect.printWidth, rect.printHeight);
  const width = (diameterIn / matWidth) * 100;
  const height = (diameterIn / matHeight) * 100;
  return {
    ...rect,
    left: rect.left + (rect.width - width) / 2,
    top: rect.top + (rect.height - height) / 2,
    width,
    height,
  };
};

const getNumberStyle = (openingType) => {
  if (openingType === 'oval' || openingType === 'circle') {
    return { left: '-6px', top: '-6px' };
  }
  return {};
};

const CroppedThumbnail = ({ photo, crop, aspect, className, style }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const src = photo?.attributes?.presignedPhotoUrl
      || photo?.attributes?.smallestPhoto
      || photo?.attributes?.url;
    if (!canvas || !crop || !src) return undefined;

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const ctx = canvas.getContext('2d');
      const sx = (crop.x / 100) * img.naturalWidth;
      const sy = (crop.y / 100) * img.naturalHeight;
      const sw = (crop.width / 100) * img.naturalWidth;
      const sh = (crop.height / 100) * img.naturalHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [photo, crop]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={Math.round(300 / aspect)}
      className={className}
      style={style}
    />
  );
};

const MatOpening = ({ rect, photo, crop, openingId, onDrop, isOver, onDragEnter, onDragLeave, openingType, onEditCrop}) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = () => onDragEnter(openingId);

  const handleDrop = (e) => {
    e.preventDefault();
    const photoJson = e.dataTransfer.getData('application/json');
    if (photoJson) {
      try {
        const droppedPhoto = JSON.parse(photoJson);
        onDrop(openingId, droppedPhoto);
      } catch (_) {}
    }
  };

  const shapeStyle = getShapeStyle(openingType);
  const numberStyle = getNumberStyle(openingType);

  return (
    <div
      className={`${styles.opening} ${isOver ? styles.openingOver : ''} ${photo ? styles.openingFilled : ''}`}
      style={{
        left: `${rect.left}%`,
        top: `${rect.top}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`,
        ...shapeStyle,
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
    >
      {photo && (
        <>
          <div className={styles.openingClip} style={shapeStyle}>
            {photo.attributes?.croppedPhotoUrl && (
              <img
                src={photo.attributes.croppedPhotoUrl}
                alt={photo.attributes?.photoFileName || photo.attributes?.fileName}
                className={styles.openingPhoto}
                draggable={false}
              />
            )}
            {!photo.attributes?.croppedPhotoUrl && crop && (
              <CroppedThumbnail
                photo={photo}
                crop={crop}
                aspect={rect.printWidth / rect.printHeight}
                className={styles.openingPhoto}
              />
            )}
            {!photo.attributes?.croppedPhotoUrl && !crop && (
              <img
                src={
                  photo.attributes?.smallestPhoto
                  || photo.attributes?.presignedPhotoUrl
                  || photo.attributes?.url
                }
                alt={photo.attributes?.photoFileName || photo.attributes?.fileName}
                className={styles.openingPhoto}
                draggable={false}
              />
            )}
          </div>
          <span className={styles.openingNumber} style={numberStyle}>{openingId}</span>
          <button
            type="button"
            className={styles.editCropButton}
            onClick={(e) => {
              e.stopPropagation();
              onEditCrop(openingId);
            }}
          >
            Edit Crop
          </button>
        </>
      )}
    </div>
  );
};

const MultiOpeningMatVisualization = () => {
  const { selectedProduct, openingAssignments, setOpeningAssignment, openingCrops, setOpeningCrop, form, editingLineItem } = useMultiOpeningMatStore();
  const [dragOverOpening, setDragOverOpening] = useState(null);
  const [activeCropOpeningId, setActiveCropOpeningId] = useState(null);
  const [isCropImageLoading, setIsCropImageLoading] = useState(false);
  const cropperRef = useRef(null);

  useEffect(() => {
    if (!selectedProduct?.openings) return undefined;

    let cancelled = false;
    const images = selectedProduct.openings.flatMap((opening) => {
      const photo = openingAssignments[opening.id];
      if (!photo || openingCrops[opening.id]) return [];

      const src = photo.attributes?.presignedPhotoUrl
        || photo.attributes?.smallestPhoto
        || photo.attributes?.url;
      if (!src) return [];

      const image = new Image();
      image.onload = () => {
        if (!cancelled && image.naturalWidth && image.naturalHeight) {
          setOpeningCrop(
            opening.id,
            getCenteredCrop(
              image.naturalWidth,
              image.naturalHeight,
              opening.printWidth / opening.printHeight,
            ),
          );
        }
      };
      image.src = src;
      return [image];
    });

    return () => {
      cancelled = true;
      images.forEach((image) => { image.onload = null; });
    };
  }, [selectedProduct, openingAssignments, openingCrops, setOpeningCrop]);

  const handleDrop = useCallback(
    (openingId, assignedPhoto) => {
      setOpeningAssignment(openingId, assignedPhoto);
      setDragOverOpening(null);
    },
    [setOpeningAssignment],
  );

  const handleDragEnter = useCallback((openingId) => {
    setDragOverOpening(openingId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverOpening(null);
  }, []);

  const handleEditCrop = useCallback((openingId) => {
    setIsCropImageLoading(true);
    setActiveCropOpeningId(openingId);
  }, []);

  if (!selectedProduct || !selectedProduct.openings || !selectedProduct.matWidth) return null;

  const rects = computeOpeningRects(selectedProduct);
  const matAspect = selectedProduct.matWidth / selectedProduct.matHeight;
  const filledCount = Object.values(openingAssignments).filter(Boolean).length;
  const totalCount = selectedProduct.openings.length;
  const matShapeValue = form?.inputs?.matOpeningShape?.selectedValue
    || form?.inputs?.matOpeningShapeFramed?.selectedValue;
  // Before a Mat Type/Mat Opening Shape is picked, fall back to the shape
  // implied by an Oval/Circle Deckle paper selection (instead of Standard)
  // so the preview matches the product right after it's selected.
  const paperTypeText = form?.inputs?.paperType?.selectedValueText || '';
  const deckleShape = isOvalDecklePaper(paperTypeText)
    ? 'oval'
    : isCircleDecklePaper(paperTypeText)
      ? 'circle'
      : undefined;
  const openingType = matShapeValue && matShapeValue !== 'none' ? matShapeValue : (deckleShape || 'standard');

  const activeOpening = selectedProduct.openings.find((o) => o.id === activeCropOpeningId);
  const activeCropPhoto = activeCropOpeningId != null
    ? openingAssignments[activeCropOpeningId]
    : null;

  const handleActiveCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    const cropData = cropper.getData();
    const imageData = cropper.getImageData();
    const originalWidth = imageData?.naturalWidth;
    const originalHeight = imageData?.naturalHeight;
    if (!originalWidth || !originalHeight) return;

    setOpeningCrop(activeCropOpeningId, {
      x: ((cropData.x < 0 ? 0 : cropData.x) / originalWidth) * 100,
      y: ((cropData.y < 0 ? 0 : cropData.y) / originalHeight) * 100,
      width: (cropData.width / originalWidth) * 100,
      height: (cropData.height / originalHeight) * 100,
    }, true);
  };

  if (activeOpening && activeCropPhoto) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.cropViewHeader}>
          <p className={styles.instructions}>Adjust the photo for opening {activeCropOpeningId}.</p>
          <button
            type="button"
            className={styles.doneButton}
            onClick={() => setActiveCropOpeningId(null)}
          >
            Back to Mat
          </button>
        </div>
        <div className={styles.cropImageWrapper}>
          {isCropImageLoading && (
            <div className={styles.cropLoadingOverlay}>
              <Loading message="Loading photo..." />
            </div>
          )}
          <PhotoCropper
            photo={activeCropPhoto}
            cropperRef={cropperRef}
            onCrop={handleActiveCrop}
            aspectRatioOverride={activeOpening.printWidth / activeOpening.printHeight}
            cropShapeOverride={openingType === 'oval' || openingType === 'circle' ? openingType : undefined}
            initialCrop={openingCrops[activeCropOpeningId] || null}
            onReady={() => setIsCropImageLoading(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {!editingLineItem && (
        <p className={styles.instructions}>
          Drag photos from the tray below into each opening.&nbsp;
          <strong>{filledCount}/{totalCount}</strong> filled.
        </p>
      )}

      <div className={styles.matContainer}>
        <div
          className={styles.mat}
          style={{
            aspectRatio: matAspect,
            width: `min(100%, calc(${matAspect} * 100cqh))`,
          }}
        >
          {rects.map((rect) => (
            <MatOpening
              key={rect.id}
              rect={openingType === 'circle' ? getCircleRect(rect, selectedProduct.matWidth, selectedProduct.matHeight) : rect}
              openingId={rect.id}
              photo={openingAssignments[rect.id] || null}
              crop={openingCrops[rect.id] || null}
              isOver={dragOverOpening === rect.id}
              onDrop={handleDrop}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              openingType={openingType}
              onEditCrop={handleEditCrop}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MultiOpeningMatVisualization;
